/**
 * Authentication Storage Service
 *
 * Provides a unified interface for storing authentication data.
 * This abstraction allows seamless transition from localStorage to cookies.
 *
 * Usage:
 *   import { authStorage } from 'util/authStorage';
 *   authStorage.setCredentials(credentials);
 *   const credentials = authStorage.getCredentials();
 */

import {
  setAuthCredentials,
  getAuthCredentials,
  clearAuthCookies,
  setSelectedBranch as setCookieSelectedBranch,
  getSelectedBranch as getCookieSelectedBranch,
  clearSelectedBranch,
  getAccessToken,
  migrateFromLocalStorage,
  needsMigration,
  updateAccessToken as updateCookieAccessToken,
} from "./cookieUtils";

import { CREDENTIALS, SELECTED_BRANCH_DATA } from "../shared/constants";

// Storage configuration
const STORAGE_CONFIG = {
  USE_COOKIES: true, // Set to true to use cookies, false for localStorage
  AUTO_MIGRATE: true, // Automatically migrate from localStorage to cookies
};

/**
 * Initialize storage (run migration if needed)
 */
function initializeStorage() {
  if (STORAGE_CONFIG.USE_COOKIES && STORAGE_CONFIG.AUTO_MIGRATE) {
    if (needsMigration()) {
      console.log("Migrating auth data from localStorage to cookies...");
      migrateFromLocalStorage();

      // Clear localStorage after successful migration
      localStorage.removeItem(CREDENTIALS);
      localStorage.removeItem(SELECTED_BRANCH_DATA);
    }
  }
}

/**
 * Storage abstraction layer
 * Provides a consistent API regardless of underlying storage mechanism
 */
export const authStorage = {
  /**
   * Set user credentials
   * @param {object} credentials - User credentials object
   * @param {object} expirationTimes - Expiration times for different tokens (optional)
   * @param {Date} expirationTimes.accessToken - Access token expiration date
   * @param {Date} expirationTimes.refreshToken - Refresh token expiration date
   * @param {Date} expirationTimes.credentials - Credentials expiration date
   */
  setCredentials(credentials, expirationTimes) {
    if (!credentials) {
      console.error("Cannot set empty credentials");
      return false;
    }

    if (STORAGE_CONFIG.USE_COOKIES) {
      return setAuthCredentials(credentials, expirationTimes);
    } else {
      try {
        localStorage.setItem(CREDENTIALS, JSON.stringify(credentials));
        return true;
      } catch (error) {
        console.error("Error setting credentials to localStorage:", error);
        return false;
      }
    }
  },

  /**
   * Get user credentials
   * @returns {object|null} - Credentials object or null
   */
  getCredentials() {
    if (STORAGE_CONFIG.USE_COOKIES) {
      return getAuthCredentials();
    } else {
      try {
        const credentials = localStorage.getItem(CREDENTIALS);
        return credentials ? JSON.parse(credentials) : null;
      } catch (error) {
        console.error("Error getting credentials from localStorage:", error);
        return null;
      }
    }
  },

  /**
   * Get access token
   * @returns {string|null}
   */
  getAccessToken() {
    if (STORAGE_CONFIG.USE_COOKIES) {
      return getAccessToken();
    } else {
      try {
        const credentials = localStorage.getItem(CREDENTIALS);
        if (credentials) {
          const parsed = JSON.parse(credentials);
          return parsed.accessToken || null;
        }
        return null;
      } catch (error) {
        console.error("Error getting access token:", error);
        return null;
      }
    }
  },

  /**
   * Set selected branch
   * @param {object} branchData - Branch data object
   * @param {number|Date} [expiration] - Expiration time for the branch data (optional)
   */
  setSelectedBranch(branchData, expiration) {
    if (!branchData) return false;

    if (STORAGE_CONFIG.USE_COOKIES) {
      return setCookieSelectedBranch(branchData, expiration);
    } else {
      try {
        localStorage.setItem(SELECTED_BRANCH_DATA, JSON.stringify(branchData));
        return true;
      } catch (error) {
        console.error("Error setting selected branch:", error);
        return false;
      }
    }
  },

  /**
   * Get selected branch
   * @returns {object|null}
   */
  getSelectedBranch() {
    if (STORAGE_CONFIG.USE_COOKIES) {
      return getCookieSelectedBranch();
    } else {
      try {
        const branch = localStorage.getItem(SELECTED_BRANCH_DATA);
        if (branch && branch !== "undefined") {
          return JSON.parse(branch);
        }
        return null;
      } catch (error) {
        console.error("Error getting selected branch:", error);
        return null;
      }
    }
  },

  /**
   * Clear all authentication data
   */
  clear() {
    if (STORAGE_CONFIG.USE_COOKIES) {
      clearAuthCookies();
    }

    // Always clear localStorage as well (for cleanup/backward compatibility)
    try {
      localStorage.removeItem(CREDENTIALS);
      localStorage.removeItem(SELECTED_BRANCH_DATA);
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }
  },

  /**
   * Clear all storage (including non-auth data)
   */
  clearAll() {
    if (STORAGE_CONFIG.USE_COOKIES) {
      clearAuthCookies();
    }

    // Clear localStorage
    try {
      localStorage.clear();
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }
  },

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    const credentials = this.getCredentials();
    return credentials && credentials.role ? true : false;
  },

  /**
   * Get specific credential field
   * @param {string} field - Field name (e.g., 'userId', 'merchantId')
   * @returns {any|null}
   */
  getCredentialField(field) {
    const credentials = this.getCredentials();
    return credentials ? credentials[field] : null;
  },

  /**
   * Update access token in cookies
   * @param {string} accessToken - New access token
   * @param {Date} [expirationTime] - Expiration time for the access token (optional)
   */
  updateAccessToken(accessToken, expirationTime) {
    if (STORAGE_CONFIG.USE_COOKIES) {
      return updateCookieAccessToken(accessToken, expirationTime);
    } else {
      try {
        const credentials = JSON.parse(localStorage.getItem(CREDENTIALS) || '{}');
        credentials.accessToken = accessToken;
        localStorage.setItem(CREDENTIALS, JSON.stringify(credentials));
        return true;
      } catch (error) {
        console.error("Error updating access token in localStorage:", error);
        return false;
      }
    }
  },
};

// Initialize storage on module load
initializeStorage();

// Export for backward compatibility and utility
export default authStorage;

/**
 * Legacy support - Backwards compatible functions
 * These maintain the old localStorage API but use the new storage abstraction
 */
export const legacyStorage = {
  setItem(key, value) {
    if (key === CREDENTIALS) {
      try {
        const credentials = typeof value === "string" ? JSON.parse(value) : value;
        return authStorage.setCredentials(credentials);
      } catch (error) {
        console.error("Error in legacy setItem:", error);
        return false;
      }
    } else if (key === SELECTED_BRANCH_DATA) {
      try {
        const branch = typeof value === "string" ? JSON.parse(value) : value;
        return authStorage.setSelectedBranch(branch);
      } catch (error) {
        console.error("Error in legacy setItem:", error);
        return false;
      }
    } else {
      // For non-auth data, use localStorage
      localStorage.setItem(key, value);
    }
  },

  getItem(key) {
    if (key === CREDENTIALS) {
      const credentials = authStorage.getCredentials();
      return credentials ? JSON.stringify(credentials) : null;
    } else if (key === SELECTED_BRANCH_DATA) {
      const branch = authStorage.getSelectedBranch();
      return branch ? JSON.stringify(branch) : null;
    } else {
      // For non-auth data, use localStorage
      return localStorage.getItem(key);
    }
  },

  removeItem(key) {
    if (key === CREDENTIALS || key === SELECTED_BRANCH_DATA) {
      authStorage.clear();
    } else {
      localStorage.removeItem(key);
    }
  },

  clear() {
    authStorage.clearAll();
  },
};
